import { Button, IconButton } from "@material-ui/core"
import VisibilityIcon from "@material-ui/icons/Visibility"
import { createColumnHelper } from "@tanstack/react-table"
import { getDemoUsers } from "~/actions/users"
import BasicSortHeader from "~/components/Analytics/BasicSortHeader"
import BasicTable from "~/components/BasicTable"
import PermissionsWrapper from "~/components/Common/PermissionsWrapper"
import AppContext from "~/contexts/App"
import dayjs from "dayjs"
import { useContext, useEffect, useState } from "react"
import { useInfiniteQuery } from "react-query"

import DemoUsersDialog from "../DemoDialog"
import DemoTokenDialog from "../DemoTokenDialog"

const columnHelper = createColumnHelper()

const columns = [
  columnHelper.accessor((row) => row.id, {
    id: "id",
    header: ({ column }) => <BasicSortHeader column={column} label="ID" />,
  }),
  columnHelper.accessor((row) => row?.first_name, {
    first_name: "first_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="First Name" />
    ),
    id: "first_name",
    cell: (props) => {
      const splitName = props?.row?.original?.first_name?.split(" ")
      return (
        <span className="flex flex-col">
          {splitName?.map((sso, i) => (
            <span key={i}>{sso}</span>
          ))}
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.last_name, {
    last_name: "last_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Last Name" />
    ),
    id: "last_name",
  }),
  columnHelper.accessor((row) => row.username, {
    username: "username",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Username" />
    ),
    id: "username",
  }),
  columnHelper.accessor((row) => row.last_online, {
    last_online: "last_online",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Last Online" />
    ),
    id: "last_online",
    cell: (props) => {
      return (
        <span>
          {
            dayjs(props.row.original.last_online).format("MM/DD/YYYY HH:mm") // '25/01/2019'
          }
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.created, {
    created: "created",
    header: ({ column }) => <BasicSortHeader column={column} label="Created" />,
    id: "created",
    cell: (props) => {
      return (
        <span>
          {
            dayjs(props.row.original.created).format("MM/DD/YYYY HH:mm") // '25/01/2019'
          }
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.progressCourses, {
    progressCourses: "progressCourses",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Courses in Progress" />
    ),
    id: "progressCourses",
    cell: (props) => {
      return (
        <span>
          {
            props.row.original.progressCourses.length // '25/01/2019'
          }
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.completedCourses, {
    completedCourses: "completedCourses",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Completed Courses" />
    ),
    id: "completedCourses",
    cell: (props) => {
      return (
        <span>
          {
            props.row.original.completedCourses?.length // '25/01/2019'
          }
        </span>
      )
    },
  }),
  columnHelper.accessor(
    (row) => [
      row.github_id,
      row.amazon_id,
      row.facebook_id,
      row.google_id,
      row.meta_quest_id,
    ],
    {
      github_id: "github_id ",
      amazon_id: "amazon_id",
      facebook_id: "facebook_id",
      google_id: "google_id",
      meta_quest_id: "meta_quest_id",
      header: ({ column }) => (
        <BasicSortHeader column={column} label="SSO Connected" />
      ),
      id: "sso_connected",
      cell: (props) => {
        const userSSOList = []

        if (props.row.original.github_id) {
          userSSOList.push("Github")
        }
        if (props.row.original.amazon_id) {
          userSSOList.push("Amazon")
        }
        if (props.row.original.facebook_id) {
          userSSOList.push("Facebook")
        }
        if (props.row.original.google_id) {
          userSSOList.push("Google")
        }
        if (props.row.original.meta_quest_id) {
          userSSOList.push("Meta Quest")
        }

        return (
          <span className="flex flex-col">
            {userSSOList.map((sso, i) => (
              <span key={i}>{sso}</span>
            ))}
          </span>
        )
      },
    },
  ),
]

export default function DemoUsers() {
  const [inputDialogOpen, setInputDialogOpen] = useState(false)
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false)
  const [firstName, setFirstName] = useState(null)
  const [lastName, setLastName] = useState(null)
  const [currentRefetchId, setCurrentRefetchId] = useState(null)
  const { openDialog, setPageSubTitle, setPageTitle } = useContext(AppContext)

  const [showMeta, setShowMeta] = useState(false)
  const [showCourse, setShowCourse] = useState(false)
  // ? ------------ new --------------
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)

  let dataFetch = {
    limit: pageSize,
  }

  const columnActions = (row) => {
    return (
      <td className="flex relative pr-3">
        <IconButton
          key={row.id}
          onClick={() => openDialog("userDetailsDialog", row.original)}
          color="secondary"
          className="h-full"
        >
          <VisibilityIcon />
        </IconButton>
        <PermissionsWrapper permissionGroup="super">
          <button
            onClick={() => {
              setCurrentRefetchId(row.original.id)
              setFirstName(row.original.first_name)
              setLastName(row.original.last_name)
              setTokenDialogOpen(true)
            }}
            className="underline text-sm hover:text-blue"
          >
            Refetch
          </button>
        </PermissionsWrapper>
      </td>
    )
  }

  const { data, fetchNextPage, isFetching, refetch, hasNextPage } =
    useInfiniteQuery({
      queryKey: ["users", dataFetch],
      queryFn: async ({ pageParam = 0 }) => {
        return await getDemoUsers({ ...dataFetch, offset: pageParam })
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPageData = lastPage.nextOffset
        return nextPageData
      },
    })
  // ? ------------ new --------------

  useEffect(() => {
    setPageTitle("Demo Users")
    setPageSubTitle("")
  }, [])

  const handleCloseInput = () => {
    setFirstName(null)
    setLastName(null)
    setInputDialogOpen(false)
  }
  const handleTransferInput = () => {
    setInputDialogOpen(false)
    setTokenDialogOpen(true)
  }
  const handleCloseToken = () => {
    setTokenDialogOpen(false)
    setCurrentRefetchId(null)
    setFirstName(null)
    setLastName(null)
    setShowCourse(false)
    setShowMeta(false)
  }

  useEffect(() => {
    setPageSubTitle(data?.length)
  }, [data])

  return (
    <>
      <div
        style={{
          padding: "40px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <div className="">
          <p style={{ fontSize: 18, marginBottom: 0 }}>Create Demo User</p>
        </div>
        <PermissionsWrapper permissionGroup="super">
          <Button
            variant="contained"
            color="primary"
            onClick={() => setInputDialogOpen(true)}
            className="max-w-[400px]"
          >
            Create Demo User
          </Button>
        </PermissionsWrapper>
      </div>
      <div className="px-8 pb-20 h-[calc(100vh-310px)]">
        <BasicTable
          columns={columns}
          data={data}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          refetch={refetch}
          hasNextPage={hasNextPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          columnActions={columnActions}
        />
      </div>
      <DemoUsersDialog
        open={inputDialogOpen}
        handleTransferInput={handleTransferInput}
        handleClose={handleCloseInput}
        setTokenDialogOpen={setTokenDialogOpen}
        firstName={firstName}
        lastName={lastName}
        setFirstName={setFirstName}
        setLastName={setLastName}
      />
      <DemoTokenDialog
        open={tokenDialogOpen}
        currentRefetchId={currentRefetchId}
        setCurrentRefetchId={setCurrentRefetchId}
        setTokenDialogOpen={setTokenDialogOpen}
        handleCloseToken={handleCloseToken}
        firstName={firstName}
        lastName={lastName}
        setFirstName={setFirstName}
        setLastName={setLastName}
        showCourse={showCourse}
        showMeta={showMeta}
        setShowCourse={setShowCourse}
        setShowMeta={setShowMeta}
      ></DemoTokenDialog>
    </>
  )
}
